import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { createListCollection } from "@chakra-ui/react";
import type { SimpleSchemaField } from "@/components/Document/DocumentSchema";
import type { SchemaPublic } from "@/client";

interface DBSchema {
  id: string;
  schema_name: string;
  description: string;
  schema_content: SimpleSchemaField[];
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface SchemaContextType {
  // 상태
  selectedTemplate: string;
  viewMode: "table" | "code" | "result";
  dbSchemas: DBSchema[];
  isSaveDialogOpen: boolean;

  // 액션
  setSelectedTemplate: (template: string) => void;
  setViewMode: (mode: "table" | "code" | "result") => void;
  setIsSaveDialogOpen: (open: boolean) => void;
  fetchSchemas: () => Promise<void>;
  handleTemplateChange: (templateName: string) => void;

  // 계산된 값
  dynamicSchemasConfig: { label: string; value: string }[];
  dynamicChakraSchemaList: ReturnType<
    typeof createListCollection<{ label: string; value: string }>
  >;

  // 유틸리티 함수
  convertDBSchemaToSchemaPublic: (dbSchema: DBSchema) => SchemaPublic;
}

const SchemaContext = createContext<SchemaContextType | undefined>(undefined);

export const useSchemaContext = () => {
  const context = useContext(SchemaContext);
  if (!context) {
    throw new Error("useSchemaContext must be used within SchemaProvider");
  }
  return context;
};

interface SchemaProviderProps {
  children: React.ReactNode;
  onTemplateChange?: (templateName: string) => void;
  onTemplateLoad?: (templateName: string) => void;
}

export const SchemaProvider = ({
  children,
  onTemplateChange,
  onTemplateLoad,
}: SchemaProviderProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    "00000000-0000-0000-0000-000000000000"
  );
  const [viewMode, setViewMode] = useState<"table" | "code" | "result">(
    "table"
  );
  const [dbSchemas, setDbSchemas] = useState<DBSchema[]>([]);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  // DB에서 스키마 목록 가져오기
  const fetchSchemas = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return [];
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/schema/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`스키마 조회 실패: ${response.status}`);
      }

      const data = await response.json();
      const schemas = data.data || [];
      setDbSchemas(schemas);
      return schemas;
    } catch (error) {
      return [];
    }
  }, []); // 의존성 배열을 비워서 무한 루프 방지

  // 컴포넌트 마운트 시 스키마 목록 가져오기
  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  const handleTemplateChange = useCallback(
    async (templateName: string) => {
      // 사용자 정의 스키마가 아닌 경우, 스키마가 목록에 있는지 확인
      if (templateName !== "00000000-0000-0000-0000-000000000000") {
        const schemaExists = dbSchemas.find(
          (schema) => schema.id === templateName
        );
        if (!schemaExists) {
          await fetchSchemas();
        }
      }

      setSelectedTemplate(templateName);

      // localStorage에 저장
      localStorage.setItem("selectedTemplate", templateName);

      if (onTemplateChange) {
        onTemplateChange(templateName);
      }
      if (onTemplateLoad) {
        onTemplateLoad(templateName);
      }
    },
    [onTemplateChange, onTemplateLoad, dbSchemas, fetchSchemas]
  );

  // 동적 스키마 설정 생성
  const dynamicSchemasConfig = useMemo(() => {
    const config = dbSchemas.map((db) => ({
      label: db.schema_name,
      value: db.id,
    }));
    config.push({
      label: "사용자 정의",
      value: "00000000-0000-0000-0000-000000000000",
    });
    return config;
  }, [dbSchemas]);

  // 동적 chakra 스키마 리스트 생성
  const dynamicChakraSchemaList = useMemo(() => {
    return createListCollection({
      items: dynamicSchemasConfig,
    });
  }, [dynamicSchemasConfig]);

  const convertDBSchemaToSchemaPublic = useCallback(
    (dbSchema: DBSchema): SchemaPublic => {
      return {
        id: dbSchema.id,
        schema_name: dbSchema.schema_name,
        description: dbSchema.description,
        schema_content: [], // EditSchema에서는 사용하지 않으므로 빈 배열로 설정
        created_at: dbSchema.created_at,
        updated_at: dbSchema.updated_at,
        user_id: dbSchema.user_id,
      };
    },
    []
  );

  // setViewMode 함수를 래핑하여 로깅 추가
  const setViewModeWithLogging = useCallback(
    (mode: "table" | "code" | "result") => {
      setViewMode(mode);
    },
    []
  );

  const value = {
    selectedTemplate,
    viewMode,
    dbSchemas,
    isSaveDialogOpen,
    setSelectedTemplate,
    setViewMode: setViewModeWithLogging,
    setIsSaveDialogOpen,
    fetchSchemas,
    handleTemplateChange,
    dynamicSchemasConfig,
    dynamicChakraSchemaList,
    convertDBSchemaToSchemaPublic,
  };

  return (
    <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>
  );
};
